import { useCallback, useEffect, useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { REPLY_STATUSES, inputCls } from "../../lib/outreach";
import EmailCard from "./EmailCard";
import LeadRepliesThread from "./LeadRepliesThread";

export default function LeadOutreachSection({ lead, onLeadChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [emails, setEmails] = useState(null);
  const [savingRs, setSavingRs] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach/emails", { params: { lead_id: lead.id, status: "all" } });
      setEmails(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setEmails([]);
    }
  }, [lead.id]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = async () => {
    await load();
    const { data } = await api.get(`/leads/${lead.id}`);
    onLeadChange(data);
  };

  const setReplyStatus = async (reply_status) => {
    setSavingRs(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { reply_status });
      onLeadChange(data);
      toast.success("Reply status updated");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingRs(false);
    }
  };

  const rs = lead.reply_status || "None";

  return (
    <section data-testid="lead-outreach-section" className="rounded-2xl border border-slate-800 bg-[#131C31] p-6 mt-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-lg inline-flex items-center gap-2"><Mail className="w-5 h-5 text-amber-400" /> Outreach</h2>
          <p className="text-xs text-slate-400 mt-0.5">Email sequence sent from alex@kingofit.ca</p>
        </div>
        <div className="w-full sm:w-56">
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Reply status {savingRs && <Loader2 className="inline w-3 h-3 animate-spin" />}</label>
          <select data-testid="lead-reply-status-select" disabled={!isAdmin || savingRs} className={inputCls} value={rs} onChange={(e) => setReplyStatus(e.target.value)}>
            {REPLY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {rs === "Out of office" && (
            <p data-testid="lead-ooo-return" className="text-[11px] text-amber-300 mt-1">
              Returns {lead.ooo_return_date || "—"} · paused until {lead.outreach_paused_until ? new Date(lead.outreach_paused_until).toLocaleDateString("en-CA") : "—"}
            </p>
          )}
        </div>
      </div>

      {emails === null ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading outreach…</div>
      ) : emails.length === 0 ? (
        <p data-testid="lead-outreach-empty" className="text-sm text-slate-500 py-4">No outreach emails yet. Drafts created by your outreach assistant will appear here.</p>
      ) : (
        <div className="space-y-3">
          {emails.map((e) => <EmailCard key={e.id} email={e} isAdmin={isAdmin} onChange={refreshAll} />)}
        </div>
      )}
      <LeadRepliesThread lead={lead} isAdmin={isAdmin} onReplied={refreshAll} />
    </section>
  );
}
